/* eslint-disable no-console */
// Standard logger of the control panel. The only place that may call `console.*` (ESLint `no-console` enforces it elsewhere).
//
// Format:  2026-10-21T15:04:05.123Z INFO  [scope] message  {data}
// Levels:  debug, info, warn, error
//
// REACT_APP_ENABLE_LOGS decides at BUILD time (Create React App inlines it):
//   "true"            all levels are printed (use it on beta: scripts/deploy-hosting.sh beta --logs)
//   anything else     only `error` is printed; debug/info/warn do nothing and the HTTP / global error hooks are not installed
// Everything printed goes through `sanitize`: passwords, tokens, keys, Authorization and similar fields are replaced by
// <redacted>, e-mails are masked and long values are truncated, so a log never carries a secret.

const ENABLED = process.env.REACT_APP_ENABLE_LOGS === "true";

const SENSITIVE_KEY = /pass(word)?|pwd|token|secret|authorization|api[-_]?key|credential|cookie|session|private|^iv$|^key$/i;
const MAX_DEPTH = 4;
const MAX_ITEMS = 20;
const MAX_STRING = 300;

function redactString(text) {
  let out = String(text)
    .replace(/Bearer\s+[A-Za-z0-9._~+/=-]+/gi, "Bearer <redacted>")
    .replace(/eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]*/g, "<jwt>")
    .replace(/([?&;]|\b)(pass(?:word)?|token|key|api[-_]?key|secret|auth|access_token|id_token)=([^&\s"']+)/gi, "$1$2=<redacted>")
    .replace(/([A-Za-z0-9._%+-])[A-Za-z0-9._%+-]*@([A-Za-z0-9.-]+\.[A-Za-z]{2,})/g, "$1***@$2");
  if (out.length > MAX_STRING) out = out.slice(0, MAX_STRING) + `... (${out.length - MAX_STRING} more characters)`;
  return out;
}

function sanitize(value, depth = 0, seen = new WeakSet()) {
  if (value === null || value === undefined) return value;
  switch (typeof value) {
    case "string": return redactString(value);
    case "number": case "boolean": return value;
    case "function": return "[Function]";
    case "symbol": return value.toString();
    default: break;
  }
  if (value instanceof Error) {
    return { name: value.name, message: redactString(value.message), code: value.code, stack: ENABLED ? redactString(value.stack || "") : undefined };
  }
  if (typeof Node !== "undefined" && value instanceof Node) return "[DOM node]";
  if (typeof Event !== "undefined" && value instanceof Event) return `[Event ${value.type}]`;
  if (seen.has(value)) return "[Circular]";
  if (depth >= MAX_DEPTH) return Array.isArray(value) ? `[Array(${value.length})]` : "[Object]";
  seen.add(value);
  if (Array.isArray(value)) {
    const items = value.slice(0, MAX_ITEMS).map((v) => sanitize(v, depth + 1, seen));
    if (value.length > MAX_ITEMS) items.push(`... ${value.length - MAX_ITEMS} more items`);
    return items;
  }
  const out = {};
  const keys = Object.keys(value);
  keys.slice(0, MAX_ITEMS).forEach((k) => {
    out[k] = SENSITIVE_KEY.test(k) ? "<redacted>" : sanitize(value[k], depth + 1, seen);
  });
  if (keys.length > MAX_ITEMS) out["..."] = `${keys.length - MAX_ITEMS} more fields`;
  return out;
}

const OUTPUT = { debug: "log", info: "info", warn: "warn", error: "error" };

function write(level, scope, message, data) {
  if (!ENABLED && level !== "error") return;
  const line = `${new Date().toISOString()} ${level.toUpperCase().padEnd(5)} [${scope}] ${redactString(message)}`;
  if (data === undefined) console[OUTPUT[level]](line);
  else console[OUTPUT[level]](line, sanitize(data));
}

// const log = createLogger("ControlPoint");  log.info("QR token updated", { controlPointId });
export function createLogger(scope = "app") {
  return {
    debug: (message, data) => write("debug", scope, message, data),
    info: (message, data) => write("info", scope, message, data),
    warn: (message, data) => write("warn", scope, message, data),
    error: (message, data) => write("error", scope, message, data),
  };
}

export const logger = createLogger("app");
export const logsEnabled = ENABLED;

// Logs every fetch (method, URL, status, time; never headers or bodies). Only when logs are enabled.
export function installNetworkLogging() {
  if (process.env.REACT_APP_ENABLE_LOGS !== "true") return;
  if (typeof window === "undefined" || !window.fetch || window.fetch.__cittyoLogged) return;
  const log = createLogger("http");
  const original = window.fetch.bind(window);
  const logged = function (input, init) {
    const method = (init && init.method) || (input && input.method) || "GET";
    const url = typeof input === "string" ? input : (input && input.url) || String(input);
    const started = performance.now();
    return original(input, init).then(
      (response) => {
        const text = `${method} ${url} -> ${response.status} (${Math.round(performance.now() - started)} ms)`;
        if (response.ok) log.info(text);
        else log.warn(text);
        return response;
      },
      (error) => {
        log.error(`${method} ${url} failed after ${Math.round(performance.now() - started)} ms`, error);
        throw error;
      }
    );
  };
  logged.__cittyoLogged = true;
  window.fetch = logged;
}

// Logs uncaught errors and unhandled promise rejections. Only when logs are enabled.
export function installGlobalErrorLogging() {
  if (process.env.REACT_APP_ENABLE_LOGS !== "true") return;
  if (typeof window === "undefined") return;
  const log = createLogger("window");
  window.addEventListener("error", (event) => log.error("Uncaught error", event.error || event.message));
  window.addEventListener("unhandledrejection", (event) => log.error("Unhandled promise rejection", event.reason));
}

// One line with what is running, to know in the browser console which build and which backend/Firebase it uses.
export function logStartup() {
  if (process.env.REACT_APP_ENABLE_LOGS !== "true") return;
  logger.info("Cittyo control panel started", {
    version: process.env.REACT_APP_VERSION || "dev",
    build: process.env.REACT_APP_BUILD_INFO || "unknown",
    mode: process.env.NODE_ENV,
    api: process.env.REACT_APP_BASE_URL,
    firebaseProject: process.env.REACT_APP_FB_PROJECT_ID,
    controlPoints: process.env.REACT_APP_SHOW_CONTROL_POINTS === "true",
  });
}
